import React from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';

import ExternalCSVFile from './ExternalCSVFile';
import ExternalFileChip from './ExternalFileChip';
import ExternalFileIFrame from './ExternalFileIFrame';
import ExternalFileImage from './ExternalFileImage';
import ExternalTextFile from './ExternalTextFile';

interface ExternalFilePaneContentProps {
  scratchFile: GetAnalysisScratchFile;
}

export function renderFilePreview(scratchFile: GetAnalysisScratchFile): JSX.Element | null {
  switch (scratchFile.mimeType) {
    // In the future, we could support `HTML` as a first-class `EXTERNAL_FILE_TYPE`, so that embedded HTML
    // previews are supported across the whole platform (i.e., for all `ExternalFile` callers). For now, we
    // scope it down to Analyses.
    case 'text/html':
      return (
        <ExternalFileIFrame
          scratchFile={scratchFile}
          externalFileWarning="This block contains a file that is external to Benchling."
          // We allow scripts in the iframe because they are needed to render HTML reports generated by JMP.
          dangerouslyAllowScriptsInIFrame={true}
          // File ExternalFiles do not have their Content-Type metadata set, so they default to
          // application/octet-stream. Therefore, we need to tell the S3 signing endpoint to override the
          // Content-Type header to text/html.
          dangerouslyOverrideHTMLFileContentTypeHeader={true}
        />
      );
    case 'application/json':
    case 'text/plain':
      return <ExternalTextFile scratchFile={scratchFile} />;
    case 'text/csv':
    case 'text/tsv':
      return <ExternalCSVFile scratchFile={scratchFile} />;
    case 'image/jpeg':
    case 'image/png':
      return <ExternalFileImage scratchFile={scratchFile} />;
    default:
      return null;
  }
}

/**
 * Render `Pane` content for the given `ExternalFile` for Analysis use cases
 *
 * As of writing, those use cases are:
 *   - Embedded HTML for HTML reports
 *   - Text files for plain text content (.txt, .rtf files)
 *   - CSV files for CSV content (.csv, .tsv files)
 *   - File chip for JMP and JRP files
 *
 * This component is intentionally written to be `File`-agnostic, so that it may share code with other
 * `ExternalFile` callers more easily in the future.
 */
export default function ExternalFilePaneContent({scratchFile}: ExternalFilePaneContentProps): JSX.Element {
  const filePreview = renderFilePreview(scratchFile);
  return filePreview ?? <ExternalFileChip scratchFile={scratchFile} />;
}
